import React, { createContext, useState, useEffect, ReactNode } from 'react';

type LanguageContextType = {
  language: 'en' | 'ro';
  setLanguage: (language: 'en' | 'ro') => void;
};

// Create context with default values
export const LanguageContext = createContext<LanguageContextType>({
  language: 'ro',
  setLanguage: () => {},
});

type LanguageProviderProps = {
  children: ReactNode;
};

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<'en' | 'ro'>('ro');

  // Initialize language from URL or localStorage or browser settings
  useEffect(() => {
    // Check URL parameters first
    const urlParams = new URLSearchParams(window.location.search);
    const langParam = urlParams.get('lang');
    
    if (langParam === 'en' || langParam === 'ro') {
      setLanguageState(langParam as 'en' | 'ro');
      // Also update localStorage for consistency
      localStorage.setItem('wedding-language', langParam);
      return;
    }

    // Check localStorage next
    const storedLang = localStorage.getItem('wedding-language');
    if (storedLang === 'en' || storedLang === 'ro') {
      setLanguageState(storedLang as 'en' | 'ro');
      return;
    }

    // Finally, check browser language
    const browserLang = navigator.language.toLowerCase();
    const detectedLang = browserLang.startsWith('ro') ? 'ro' : 'en';
    setLanguageState(detectedLang);
    localStorage.setItem('wedding-language', detectedLang);
  }, []);

  // Custom setLanguage function that also updates localStorage
  const setLanguage = (newLanguage: 'en' | 'ro') => {
    setLanguageState(newLanguage);
    
    // Update localStorage
    localStorage.setItem('wedding-language', newLanguage);
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};